import { useState, useEffect } from 'react';
import axios from 'axios';
import { isChairmanReadOnly } from '../utils/access';

// ═══════════════════════════════════════════════════════════════
// TREASURY'S BANK RECONCILIATION (Aug 2026)
// Rendered as a tab inside Treasury.js (accounts passed down as a
// prop — no need to refetch what the parent already has). Upload
// the same statement Payments already reads for deposits; this
// screen instead walks EVERY line — debit and credit — against
// treasury_transactions, so a real "does our ledger match the
// bank's" answer is possible, not just "are deposits landing."
// ═══════════════════════════════════════════════════════════════

const API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/bank-reconciliation';

const cardStyle   = { background: 'white', border: '1px solid #ecf0f1', borderRadius: '8px', padding: '18px', marginBottom: '16px' };
const btnStyle    = (bg) => ({ background: bg, color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' });
const smBtn       = (bg) => ({ background: bg, color: 'white', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' });
const fieldStyle  = { display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '160px' };
const labelStyle  = { fontSize: '12px', color: '#7f8c8d', fontWeight: 'bold' };
const inputStyle  = { padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' };
const tableStyle  = { width: '100%', borderCollapse: 'collapse', marginTop: '10px' };
const thStyle     = { padding: '10px', textAlign: 'left', fontSize: '13px' };
const tdStyle     = { padding: '10px', borderBottom: '1px solid #eee', fontSize: '13px' };
const sectionTitle = { fontSize: '16px', fontWeight: 'bold', color: '#2c3e50' };

// The statement's own real coverage window — never a calendar month.
// Centenary's export is "start of period to now," so a run genuinely
// spans however far back the statement goes through today; showing
// it as one month would misstate what was actually checked.
function coverage(run) {
  if (!run.statement_from_date || !run.statement_to_date) return '—';
  const from = run.statement_from_date.slice(0, 10);
  const to = run.statement_to_date.slice(0, 10);
  return from === to ? from : `${from} → ${to}`;
}

function statusBadge(status) {
  const colors = { pending: '#f39c12', approved: '#27ae60', flagged_to_payments: '#8e44ad' };
  const labels = { pending: 'pending', approved: 'posted', flagged_to_payments: 'flagged to Payments' };
  return <span style={{ background: colors[status] || '#95a5a6', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{labels[status] || status}</span>;
}

function BankReconciliation({ accounts }) {
  const [accountId, setAccountId]   = useState('');
  const [file, setFile]             = useState(null);
  const [running, setRunning]       = useState(false);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');

  const [lastResult, setLastResult] = useState(null); // { run, balance_difference, unrecorded }
  const [runs, setRuns]             = useState([]);
  const [selectedRun, setSelectedRun] = useState(null); // { run, unrecorded }
  const [loadingRun, setLoadingRun] = useState(false);
  const [categoryDrafts, setCategoryDrafts] = useState({}); // { [lineId]: category string }
  const [actioning, setActioning]   = useState(null); // line id currently posting
  const readOnly = isChairmanReadOnly();

  const fetchRuns = async () => {
    try {
      const res = await axios.get(API + '/runs');
      setRuns(res.data);
    } catch (err) { /* non-fatal — history list just stays empty */ }
  };

  useEffect(() => { fetchRuns(); }, []);

  useEffect(() => {
    if (accounts.length && !accountId) setAccountId(accounts[0].id);
  }, [accounts]); // eslint-disable-line react-hooks/exhaustive-deps

  const runReconciliation = async () => {
    if (!accountId) { setError('Choose which account this statement is for'); return; }
    if (!file) { setError('Choose a statement file first'); return; }
    setError(''); setSuccess(''); setRunning(true); setLastResult(null);
    try {
      const formData = new FormData();
      formData.append('statement', file);
      formData.append('account_id', accountId);
      const res = await axios.post(API + '/run', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLastResult(res.data);
      setSelectedRun(null);
      setSuccess(`Reconciliation complete — ${res.data.run.matched_lines} matched, ${res.data.run.unmatched_lines} need review.`);
      fetchRuns();
    } catch (err) { setError(err.response?.data?.error || 'Reconciliation run failed'); }
    setRunning(false);
  };

  const viewRun = async (id) => {
    setLoadingRun(true); setError(''); setLastResult(null);
    try {
      const res = await axios.get(API + '/runs/' + id);
      setSelectedRun(res.data);
    } catch (err) { setError(err.response?.data?.error || 'Failed to load run'); }
    setLoadingRun(false);
  };

  const approveExpense = async (line) => {
    setActioning(line.id); setError('');
    try {
      await axios.post(API + '/unrecorded/' + line.id + '/approve-expense', {
        category: categoryDrafts[line.id] || null,
      });
      setSuccess('Posted as an expense.');
      if (selectedRun) viewRun(selectedRun.run.id);
      if (lastResult) refreshLastResultLine(line.id);
      fetchRuns();
    } catch (err) { setError(err.response?.data?.error || 'Failed to post expense'); }
    setActioning(null);
  };

  const flagToPayments = async (line) => {
    setActioning(line.id); setError('');
    try {
      await axios.post(API + '/unrecorded/' + line.id + '/flag-to-payments');
      setSuccess('Flagged back to Payments.');
      if (selectedRun) viewRun(selectedRun.run.id);
      if (lastResult) refreshLastResultLine(line.id);
      fetchRuns();
    } catch (err) { setError(err.response?.data?.error || 'Failed to flag line'); }
    setActioning(null);
  };

  // The just-ran result and a previously-viewed run both render the
  // same line list — after acting on a line from the just-ran view
  // (which has no run id fetch of its own yet), re-pull that one run
  // from the server so its status reflects the action immediately.
  const refreshLastResultLine = async (lineId) => {
    if (!lastResult) return;
    try {
      const res = await axios.get(API + '/runs/' + lastResult.run.id);
      setLastResult({ ...lastResult, unrecorded: res.data.unrecorded });
    } catch (err) { /* stale view until next manual refresh — non-fatal */ }
  };

  const renderLines = (lines) => (
    <table style={tableStyle}>
      <thead>
        <tr style={{ background: '#2c3e50', color: 'white' }}>
          <th style={thStyle}>Date</th>
          <th style={thStyle}>Description</th>
          <th style={thStyle}>Direction</th>
          <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
          <th style={thStyle}>Status</th>
          <th style={thStyle}>Action</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((l, i) => (
          <tr key={l.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
            <td style={tdStyle}>{l.transaction_date ? l.transaction_date.slice(0, 10) : '—'}</td>
            <td style={tdStyle}>
              {l.description}
              {l.linked_member_name && (
                <div style={{ fontSize: '11px', color: '#8e44ad', marginTop: '2px' }}>
                  possibly a fee tied to {l.linked_member_name}'s deposit ({l.linked_member_number})
                </div>
              )}
            </td>
            <td style={tdStyle}>
              <span style={{ color: l.direction === 'IN' ? '#27ae60' : '#e74c3c', fontWeight: 'bold' }}>
                {l.direction === 'IN' ? 'Money In' : 'Money Out'}
              </span>
            </td>
            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>{Number(l.amount).toLocaleString()}</td>
            <td style={tdStyle}>{statusBadge(l.status)}</td>
            <td style={tdStyle}>
              {l.status !== 'pending' && (
                <span style={{ fontSize: '11px', color: '#7f8c8d' }}>
                  {l.status === 'approved' ? 'no further action' : 'awaiting Payments'}
                </span>
              )}
              {!readOnly && l.status === 'pending' && l.direction === 'OUT' && (
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <input
                    placeholder="category (optional)"
                    value={categoryDrafts[l.id] || ''}
                    onChange={e => setCategoryDrafts({ ...categoryDrafts, [l.id]: e.target.value })}
                    style={{ ...inputStyle, padding: '4px 6px', fontSize: '12px', width: '110px' }}
                  />
                  <button onClick={() => approveExpense(l)} disabled={actioning === l.id} style={smBtn('#27ae60')}>
                    {actioning === l.id ? '...' : 'Approve as Expense'}
                  </button>
                </div>
              )}
              {!readOnly && l.status === 'pending' && l.direction === 'IN' && (
                <button onClick={() => flagToPayments(l)} disabled={actioning === l.id} style={smBtn('#8e44ad')}>
                  {actioning === l.id ? '...' : 'Flag to Payments'}
                </button>
              )}
            </td>
          </tr>
        ))}
        {!lines.length && (
          <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#27ae60' }}>Every line matched — nothing to review.</td></tr>
        )}
      </tbody>
    </table>
  );

  const balanceCard = (run, difference) => {
    const bank = run.closing_balance_bank !== null ? parseFloat(run.closing_balance_bank) : null;
    const kams = parseFloat(run.closing_balance_kams);
    const clean = Math.abs(difference) < 1;
    return (
      <div style={{ display: 'flex', gap: '16px', marginTop: '10px', marginBottom: '4px' }}>
        <div style={{ ...cardStyle, flex: 1, marginBottom: 0, textAlign: 'center' }}>
          <div style={labelStyle}>Bank's Closing Balance</div>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#2c3e50' }}>{bank !== null ? bank.toLocaleString() : '—'}</div>
        </div>
        <div style={{ ...cardStyle, flex: 1, marginBottom: 0, textAlign: 'center' }}>
          <div style={labelStyle}>KAMS Treasury Balance</div>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#2c3e50' }}>{kams.toLocaleString()}</div>
        </div>
        <div style={{ ...cardStyle, flex: 1, marginBottom: 0, textAlign: 'center', borderTop: `3px solid ${clean ? '#27ae60' : '#e74c3c'}` }}>
          <div style={labelStyle}>Difference</div>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: clean ? '#27ae60' : '#e74c3c' }}>
            {clean ? 'Balanced' : difference.toLocaleString()}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {error && <div style={{ background: '#e74c3c', color: 'white', padding: '10px 15px', borderRadius: '6px', marginBottom: '15px' }}>{error}</div>}
      {success && <div style={{ background: '#27ae60', color: 'white', padding: '10px 15px', borderRadius: '6px', marginBottom: '15px' }}>{success}</div>}

      {!readOnly && (
      <div style={cardStyle}>
        <div style={sectionTitle}>Run a Reconciliation</div>
        <p style={{ fontSize: '12px', color: '#7f8c8d', margin: '6px 0 14px' }}>
          Upload the same bank statement (PDF or CSV) you'd import in Payments — this walks every debit
          and credit against Treasury's own ledger and shows anything that doesn't match on either side.
        </p>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Account</label>
            <select style={inputStyle} value={accountId} onChange={e => setAccountId(e.target.value)}>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.account_name}</option>)}
            </select>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Statement File</label>
            <input type="file" accept=".pdf,.csv" onChange={e => setFile(e.target.files[0])} style={inputStyle} />
          </div>
          <button onClick={runReconciliation} disabled={running} style={btnStyle('#2c3e50')}>
            {running ? 'Reconciling...' : 'Run Reconciliation'}
          </button>
        </div>
      </div>
      )}

      {lastResult && (
        <div style={cardStyle}>
          <div style={sectionTitle}>Just Ran — {lastResult.run.statement_filename}</div>
          <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '2px' }}>Covers {coverage(lastResult.run)}</div>
          {balanceCard(lastResult.run, lastResult.balance_difference)}
          <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '10px' }}>
            {lastResult.run.matched_lines} of {lastResult.run.total_bank_lines} statement lines matched an existing treasury entry automatically.
          </div>
          {renderLines(lastResult.unrecorded)}
        </div>
      )}

      <div style={cardStyle}>
        <div style={sectionTitle}>Past Runs</div>
        <table style={tableStyle}>
          <thead>
            <tr style={{ background: '#ecf0f1' }}>
              <th style={thStyle}>Date Run</th>
              <th style={thStyle}>Account</th>
              <th style={thStyle}>Covers</th>
              <th style={thStyle}>Matched</th>
              <th style={thStyle}>Unmatched</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r, i) => (
              <tr key={r.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                <td style={tdStyle}>{new Date(r.run_date).toLocaleDateString()}</td>
                <td style={tdStyle}>{r.account_name}</td>
                <td style={tdStyle}>{coverage(r)}</td>
                <td style={tdStyle}>{r.matched_lines}</td>
                <td style={tdStyle}>{r.unmatched_lines}</td>
                <td style={tdStyle}><button onClick={() => viewRun(r.id)} style={smBtn('#16a085')}>View</button></td>
              </tr>
            ))}
            {!runs.length && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#95a5a6' }}>No runs yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {loadingRun && <div style={{ ...cardStyle, textAlign: 'center', color: '#7f8c8d' }}>Loading run...</div>}
      {selectedRun && (
        <div style={cardStyle}>
          <div style={sectionTitle}>Run — {selectedRun.run.statement_filename}</div>
          {balanceCard(selectedRun.run, parseFloat(selectedRun.run.closing_balance_kams) - parseFloat(selectedRun.run.closing_balance_bank || 0))}
          {renderLines(selectedRun.unrecorded)}
        </div>
      )}
    </div>
  );
}

export default BankReconciliation;
